"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

interface ModalContextValue {
  /**
   * Register a modal as open. Pass the caller's close handler so the
   * shared backdrop dismisses it when clicked.
   */
  openModal: (onClose: () => boolean | void) => void;
  /** Deregister the active modal — removes the shared backdrop. */
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

/**
 * @component ModalProvider
 * @description Renders a single shared backdrop overlay when any modal is open.
 * Each modal registers itself via `useModal().openModal(onClose)` and renders
 * its own panel into `document.body` using `createPortal` from react-dom.
 * The backdrop's onClick calls the registered `onClose` so click-to-dismiss
 * works regardless of which modal is active.
 * @param children - The application subtree.
 * @returns Provider with a shared backdrop div rendered at z-50 when a modal is open.
 */
export function ModalProvider({ children }: { children: ReactNode }) {
  // Store the close handler as a function-returning-function to avoid
  // React's useState treating a function argument as an updater.
  const [onCloseHandler, setOnCloseHandler] = useState<(() => boolean | void) | null>(null);

  const openModal = useCallback((onClose: () => boolean | void) => {
    setOnCloseHandler(() => onClose);
  }, []);

  const closeModal = useCallback(() => {
    setOnCloseHandler(null);
  }, []);

  const handleOverlayClick = useCallback(() => {
    const shouldClose = onCloseHandler?.();
    if (shouldClose !== false) {
      closeModal();
    }
  }, [onCloseHandler, closeModal]);

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {onCloseHandler !== null && (
        <div
          className="fixed inset-0 z-50 bg-near-black/50"
          aria-hidden="true"
          onClick={handleOverlayClick}
        />
      )}
    </ModalContext.Provider>
  );
}

/**
 * @hook useModal
 * @description Returns `openModal` and `closeModal` from the nearest ModalProvider.
 * Must be used within a component tree wrapped by ModalProvider.
 * @returns Modal context value with open/close functions.
 * @throws If called outside a ModalProvider.
 */
export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (ctx === null) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return ctx;
}
